"use client";

import { useMemo } from "react";
import type {
  ContentTypeFilter,
  ExploreBriefCard,
  ExploreStoryCard,
  FilterOption,
} from "../types";
import { mergeTopicItems } from "./topic-items";

// ─── Shared constants ────────────────────────────────────────

export const TYPE_OPTIONS = [
  { value: "news", label: "News" },
  { value: "workflow", label: "Workflows" },
  { value: "release", label: "Releases" },
  { value: "prompt", label: "Prompts" },
  { value: "deal", label: "Deals" },
] satisfies Array<{ value: Exclude<ContentTypeFilter, "all">; label: string }>;

// ─── Facet types ─────────────────────────────────────────────

export interface CategoryFacet {
  id: string;
  label: string;
  emoji?: string;
  count: number;
}

export interface TagFacet {
  id: string;
  label: string;
  count: number;
}

export interface ToolFacet {
  id: string;
  label: string;
  count: number;
}

export interface TopicFacet {
  id: string;
  label: string;
  kind: "tag" | "tool";
  count?: number;
}

export function buildTagFacets({
  storiesForTagFacets,
  briefsForTagFacets,
  tagDisplayNames,
}: {
  storiesForTagFacets: ExploreStoryCard[];
  briefsForTagFacets?: ExploreBriefCard[];
  tagDisplayNames: ReadonlyMap<string, string>;
}): TagFacet[] {
  const counts = new Map<string, number>();
  for (const story of storiesForTagFacets) {
    for (const tag of story.topicTags) {
      counts.set(tag.slug, (counts.get(tag.slug) ?? 0) + 1);
    }
  }
  if (briefsForTagFacets) {
    for (const brief of briefsForTagFacets) {
      for (const tag of brief.tags) {
        if (tag.kind === "TOPIC") {
          counts.set(tag.slug, (counts.get(tag.slug) ?? 0) + 1);
        }
      }
    }
  }

  const facets: TagFacet[] = [];
  for (const [slug, count] of counts.entries()) {
    if (count > 0) {
      facets.push({
        id: slug,
        label: tagDisplayNames.get(slug) ?? slug,
        count,
      });
    }
  }
  return facets.sort((a, b) => b.count - a.count);
}

export function buildToolFacets({
  storiesForTagFacets,
  briefsForTagFacets,
  toolOptions,
}: {
  storiesForTagFacets: ExploreStoryCard[];
  briefsForTagFacets?: ExploreBriefCard[];
  toolOptions: FilterOption[];
}): ToolFacet[] {
  const toolRelatedCounts = new Map<string, number>();
  for (const story of storiesForTagFacets) {
    const storyTagSet = new Set(story.allTagSlugs);
    for (const tool of toolOptions) {
      if (storyTagSet.has(tool.id)) {
        toolRelatedCounts.set(
          tool.id,
          (toolRelatedCounts.get(tool.id) ?? 0) + 1
        );
      }
    }
  }
  if (briefsForTagFacets) {
    for (const brief of briefsForTagFacets) {
      const briefTagSet = new Set(brief.allTagSlugs);
      for (const tool of toolOptions) {
        if (briefTagSet.has(tool.id)) {
          toolRelatedCounts.set(
            tool.id,
            (toolRelatedCounts.get(tool.id) ?? 0) + 1
          );
        }
      }
    }
  }

  return toolOptions
    .filter((tool) => (toolRelatedCounts.get(tool.id) ?? 0) > 0)
    .sort(
      (a, b) =>
        (toolRelatedCounts.get(b.id) ?? 0) - (toolRelatedCounts.get(a.id) ?? 0)
    )
    .map((tool) => ({
      ...tool,
      count: toolRelatedCounts.get(tool.id) ?? 0,
    }));
}

export function buildTopicFacets({
  tagFacets,
  toolFacets,
}: {
  tagFacets: TagFacet[];
  toolFacets: ToolFacet[];
}): TopicFacet[] {
  return mergeTopicItems({
    items: [
      ...tagFacets.map((tag) => ({ ...tag, kind: "tag" as const })),
      ...toolFacets.map((tool) => ({ ...tool, kind: "tool" as const })),
    ],
  });
}

// ─── Hook ────────────────────────────────────────────────────

interface UseFilterFacetsParams {
  storiesForCategoryFacets: ExploreStoryCard[];
  briefsForCategoryFacets?: Array<{ categorySlug: string | null }>;
  storiesForTagFacets: ExploreStoryCard[];
  briefsForTagFacets?: ExploreBriefCard[];
  toolOptions: FilterOption[];
  tagDisplayNames: ReadonlyMap<string, string>;
  categoryDisplayNames: ReadonlyMap<string, string>;
  categoryEmojiBySlug: ReadonlyMap<string, string>;
}

export function useFilterFacets({
  storiesForCategoryFacets,
  briefsForCategoryFacets,
  storiesForTagFacets,
  briefsForTagFacets,
  toolOptions,
  tagDisplayNames,
  categoryDisplayNames,
  categoryEmojiBySlug,
}: UseFilterFacetsParams) {
  const categoryFacets = useMemo<CategoryFacet[]>(() => {
    const counts = new Map<string, number>();
    for (const story of storiesForCategoryFacets) {
      const slug = story.categorySlug;
      if (slug) {
        counts.set(slug, (counts.get(slug) ?? 0) + 1);
      }
    }
    if (briefsForCategoryFacets) {
      for (const brief of briefsForCategoryFacets) {
        const slug = brief.categorySlug;
        if (slug) {
          counts.set(slug, (counts.get(slug) ?? 0) + 1);
        }
      }
    }
    return [...counts.entries()]
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([slug, count]) => ({
        id: slug,
        label: categoryDisplayNames.get(slug) ?? slug,
        emoji: categoryEmojiBySlug.get(slug),
        count,
      }));
  }, [
    storiesForCategoryFacets,
    briefsForCategoryFacets,
    categoryDisplayNames,
    categoryEmojiBySlug,
  ]);

  const tagFacets = useMemo<TagFacet[]>(
    () =>
      buildTagFacets({
        storiesForTagFacets,
        briefsForTagFacets,
        tagDisplayNames,
      }),
    [storiesForTagFacets, briefsForTagFacets, tagDisplayNames]
  );

  const toolFacets = useMemo<ToolFacet[]>(
    () =>
      buildToolFacets({
        storiesForTagFacets,
        briefsForTagFacets,
        toolOptions,
      }),
    [storiesForTagFacets, briefsForTagFacets, toolOptions]
  );

  const topicFacets = useMemo<TopicFacet[]>(
    () =>
      buildTopicFacets({
        tagFacets,
        toolFacets,
      }),
    [tagFacets, toolFacets]
  );

  return { categoryFacets, tagFacets, toolFacets, topicFacets };
}
